/**
 * Reservation state for the floor plan.
 *
 * Owned at the Tables level because two consumers need the same data: the rail
 * lists the bookings, and the board draws a badge on every table that has one
 * coming up. Also holds the "pick a table for this booking" assign mode.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Reservation,
    ReservationFieldDef,
    ReservationSettings,
    ReservationStatus,
} from '../interfaces';
import ReservationService, { ReservationSaveResult } from '../services/ReservationService';

/** YYYY-MM-DD in local time — reservations are date-keyed, not instant-keyed. */
export const toDateKey = (d: Date): string => {
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};

export const minutesOfTime = (time: string): number => {
    const [h, m] = (time || '0:0').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

/**
 * Whether this property organises its day into service windows. The feature is
 * opt-in, so everything that reads the windows asks here first rather than
 * inferring from `services` being non-empty — a venue can keep its sittings
 * defined while running without them.
 *
 * Settings saved before the toggle existed have no flag; for those, configured
 * windows mean it was on, so upgrading doesn't silently drop a venue's sittings.
 */
export const serviceWindowsOn = (
    settings?: Pick<ReservationSettings, 'serviceWindowsEnabled' | 'services'> | null,
): boolean => (
    !!settings && (settings.serviceWindowsEnabled ?? (settings.services?.length ?? 0) > 0)
);

/** Turn time the settings plan for a party of this size; first rule to match wins. */
export const plannedTurnMinutes = (
    settings: ReservationSettings | null | undefined,
    covers: number,
): number => {
    const rule = (settings?.durationByPartySize || [])
        .filter(r => covers >= r.fromPartySize)
        .sort((a, b) => b.fromPartySize - a.fromPartySize)[0];
    return rule ? rule.minutes : (settings?.defaultDurationMinutes ?? 90);
};

/** How long after its time a booking is still treated as "coming up". */
export const DEFAULT_LATE_GRACE_MINUTES = 15;

/**
 * The venue's grace for a booking that hasn't walked in. One number behind both
 * questions it answers — whether the board still calls a booking "coming up",
 * and when the floor is asked whether its table is still being held — so the two
 * can't drift apart.
 */
export const lateGraceMinutes = (
    settings?: Pick<ReservationSettings, 'lateGraceMinutes'> | null,
): number => settings?.lateGraceMinutes ?? DEFAULT_LATE_GRACE_MINUTES;

export interface TurnStatus {
    /** Minutes the party has been at the table. */
    elapsed: number;
    /** Minutes the sitting was planned to take. */
    planned: number;
    /** Minutes past the planned turn; 0 while still inside it. */
    over: number;
    /** Minutes the table's next booking is expected to wait; 0 when it still fits. */
    risk: number;
}

/**
 * How an occupied table is running against the turn time it was planned for, and
 * whether that puts its next booking at risk.
 *
 * Turn time is otherwise only a planning number — this is the single place that
 * measures it against the clock, so the board and the rail can't disagree about
 * which table is running over.
 */
export function turnStatus(opts: {
    /** When the order opened; null for a free table. */
    openedAt?: string | null;
    plannedMinutes: number;
    bufferMinutes?: number;
    /** The table's next booking, and only when that booking is today. */
    nextBookingTime?: string | null;
    /** The venue's grace for a booking nobody has seated yet. */
    graceMinutes?: number;
    now: number;
}): TurnStatus | null {
    const {
        openedAt,
        plannedMinutes,
        bufferMinutes = 0,
        nextBookingTime,
        graceMinutes = DEFAULT_LATE_GRACE_MINUTES,
        now,
    } = opts;
    if (!openedAt) return null;
    const opened = new Date(openedAt).getTime();
    if (Number.isNaN(opened)) return null;

    const elapsed = Math.max(0, Math.floor((now - opened) / 60000));
    const over = Math.max(0, elapsed - plannedMinutes);

    let risk = 0;
    if (nextBookingTime) {
        const d = new Date(now);
        const nowMinutes = d.getHours() * 60 + d.getMinutes();
        const bookingStart = minutesOfTime(nextBookingTime);
        // Only while the booking is still ahead, or has only just passed. Once its
        // time is well gone the problem isn't the turn any more — it's a booking
        // nobody has seated, which is not something turn time can answer.
        if (bookingStart >= nowMinutes - graceMinutes) {
            // Earliest this table can be ready: whatever is left of the turn plus
            // the turnaround. A table already over its turn is only free from now.
            const readyAt = nowMinutes + Math.max(0, plannedMinutes - elapsed) + bufferMinutes;
            risk = Math.max(0, readyAt - bookingStart);
        }
    }

    return { elapsed, planned: plannedMinutes, over, risk };
}

/** Statuses that still need a table and are worth showing on the board. */
export const UPCOMING_STATUSES: ReservationStatus[] = ['requested', 'confirmed', 'arrived'];

/** How long a called party is given to appear before the card starts nagging. */
export const WAITLIST_CALLED_GRACE_MINUTES = 5;

export interface WaitStatus {
    /** Minutes since they joined the queue. */
    waited: number;
    /** The wait they were last promised. */
    quoted: number;
    /** Minutes past that promise, 0 while still inside it. */
    over: number;
    /** Minutes left on the quote, 0 once it has run out. */
    remaining: number;
    /** True once they have been told their table is ready. */
    called: boolean;
    /** Minutes since they were called, or 0 if they haven't been. */
    calledAgo: number;
}

/**
 * Where a queue entry stands right now.
 *
 * Measured against `quotedAt` rather than `addedAt`, so re-quoting a party
 * ("sorry, another fifteen") genuinely gives them a fresh promise instead of
 * leaving them permanently over a number nobody stands behind any more.
 */
export function waitStatus(r: Reservation, now: number): WaitStatus {
    const w = r.waitlist;
    if (!w) return { waited: 0, quoted: 0, over: 0, remaining: 0, called: false, calledAgo: 0 };
    const waited = Math.max(0, Math.floor((now - new Date(w.addedAt).getTime()) / 60000));
    const sinceQuote = Math.max(0, Math.floor((now - new Date(w.quotedAt).getTime()) / 60000));
    const quoted = w.quotedMinutes;
    return {
        waited,
        quoted,
        over: Math.max(0, sinceQuote - quoted),
        remaining: Math.max(0, quoted - sinceQuote),
        called: !!w.notifiedAt,
        calledAgo: w.notifiedAt
            ? Math.max(0, Math.floor((now - new Date(w.notifiedAt).getTime()) / 60000))
            : 0,
    };
}

export interface LateStatus {
    /** Minutes past the booking's time; 0 until it arrives. */
    lateBy: number;
    /** Past the grace, and nobody has said the party is still coming. */
    late: boolean;
    /** A host has vouched for them and the hold is still running. */
    held: boolean;
    /** Minutes left on that hold. */
    heldFor: number;
}

const NOT_LATE: LateStatus = { lateBy: 0, late: false, held: false, heldFor: 0 };

/**
 * Whether a booking has been left standing.
 *
 * Derived from the clock rather than stored, for the same reason turn time is: a
 * booking that hasn't walked in is still `confirmed` — the guest may yet appear —
 * so "late" is a fact about the time, not a state anybody writes. Only today's
 * book has a live clock; on any other date every past booking would read late,
 * which is history, not a prompt.
 *
 * `heldUntil` is the one part that is written down, because it records a decision
 * somebody made: this party phoned, they are on their way, stop asking me.
 */
export function lateStatus(r: Reservation, graceMinutes: number, now: number): LateStatus {
    if (r.status !== 'requested' && r.status !== 'confirmed') return NOT_LATE;
    const clock = new Date(now);
    if (r.date !== toDateKey(clock)) return NOT_LATE;

    const nowMinutes = clock.getHours() * 60 + clock.getMinutes();
    const lateBy = Math.max(0, nowMinutes - minutesOfTime(r.time));
    const heldFor = r.heldUntil
        ? Math.ceil((new Date(r.heldUntil).getTime() - now) / 60000)
        : 0;
    return {
        lateBy,
        late: lateBy > graceMinutes && heldFor <= 0,
        held: heldFor > 0,
        heldFor: Math.max(0, heldFor),
    };
}

/** Statuses that no longer occupy the floor. */
export const CLOSED_RESERVATION_STATUSES: ReservationStatus[] = ['completed', 'cancelled', 'noShow'];

export interface ReservationStatusMeta {
    label: string;
    accent: string;
    short: string;
}

export const RESERVATION_STATUS_META: Record<ReservationStatus, ReservationStatusMeta> = {
    requested: { label: 'Requested', short: 'Req', accent: 'var(--color-warning-600)' },
    confirmed: { label: 'Confirmed', short: 'Conf', accent: 'var(--color-primary)' },
    arrived: { label: 'Arrived', short: 'Here', accent: '#0891b2' },
    seated: { label: 'Seated', short: 'Seated', accent: '#2563eb' },
    completed: { label: 'Completed', short: 'Done', accent: 'var(--color-success-700)' },
    cancelled: { label: 'Cancelled', short: 'Canc', accent: 'var(--color-foreground-subtle)' },
    noShow: { label: 'No show', short: 'No show', accent: 'var(--color-danger-600)' },
    waitlist: { label: 'Waitlist', short: 'Wait', accent: '#7c3aed' },
};

/**
 * Filter chips in the rail, in the order a service moves through them.
 * 'waitlist' is deliberately absent: the queue is its own section of the rail,
 * not a filter over the day's bookings.
 */
export const RESERVATION_STATUS_ORDER: ReservationStatus[] = [
    'requested', 'confirmed', 'arrived', 'seated', 'completed', 'noShow', 'cancelled',
];

export interface UseReservationsResult {
    dateKey: string;
    setDateKey: (d: string) => void;
    stepDate: (days: number) => void;
    goToday: () => void;
    isToday: boolean;
    reservations: Reservation[];
    /** Bookings on the selected date, in time order. */
    onDate: Reservation[];
    loading: boolean;
    fields: ReservationFieldDef[];
    settings: ReservationSettings | null;
    /** True when the venue is linked to a Prostay PMS (hotel-guest lookup). */
    prostayEnabled: boolean;
    /** True when the day is split into sittings, so the rail offers a filter. */
    serviceWindowsEnabled: boolean;
    /** Active service window id, or 'all'. */
    serviceId: string;
    setServiceId: (id: string) => void;
    statusFilter: ReservationStatus | 'all';
    setStatusFilter: (s: ReservationStatus | 'all') => void;
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    /** After the date, service, status and search filters are applied. */
    visible: Reservation[];
    /** tableId → the next reservation that table should expect today. */
    byTableId: Record<string, Reservation>;
    /** tableId → the booking currently sitting there, if it came from one. */
    seatedByTableId: Record<string, Reservation>;
    counts: Record<ReservationStatus | 'all', number>;
    kpis: {
        covers: number;
        bookings: number;
        unassigned: number;
        seated: number;
        waiting: number;
        waitingCovers: number;
    };
    /** The walk-in queue for the selected day, longest wait first. */
    waitlist: Reservation[];
    joinWaitlist: (entry: {
        partySize: number;
        quotedMinutes: number;
        guest: Reservation['guest'];
        guestNotes?: string;
        customFields?: Record<string, unknown>;
    }) => Promise<Reservation | null>;
    /** Correct a queue entry: who they are, how many, and what they were quoted. */
    updateWaiting: (r: Reservation, changes: {
        partySize: number;
        quotedMinutes: number;
        guest: Reservation['guest'];
        guestNotes?: string;
    }) => Promise<Reservation | null>;
    /** Give a waiting party a new promise, restarting their quote. */
    requoteWait: (r: Reservation, quotedMinutes: number) => Promise<void>;
    /** Record that the party has been told their table is ready. */
    callWaiting: (r: Reservation, called?: boolean) => Promise<void>;
    leaveWaitlist: (r: Reservation) => Promise<void>;
    /**
     * Vouch for a late party: hold their table for another grace period instead of
     * being asked about them again. Answers with the minutes it was held for.
     */
    holdLate: (r: Reservation) => Promise<number>;
    reload: () => Promise<void>;
    /** The booking currently being given a table, if any. */
    assigning: Reservation | null;
    startAssign: (r: Reservation) => void;
    cancelAssign: () => void;
    assignTables: (
        tableIds: string[],
        floorId?: string,
        allowOverlap?: boolean,
    ) => Promise<ReservationSaveResult>;
    updateLocal: (r: Reservation | null) => void;
    setStatus: (r: Reservation, status: ReservationStatus) => Promise<void>;
    /**
     * Move a booking to another time, another table, or both — the timeline's drag
     * lands here. Answers with the same refusal shape as any other save, because a
     * move can collide exactly as a new booking can.
     */
    move: (
        r: Reservation,
        changes: { time?: string; tableIds?: string[] },
        allowOverlap?: boolean,
    ) => Promise<ReservationSaveResult>;
}

export function useReservations(floorId?: string | null): UseReservationsResult {
    const [dateKey, setDateKey] = useState<string>(() => toDateKey(new Date()));
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState<ReservationFieldDef[]>([]);
    const [settings, setSettings] = useState<ReservationSettings | null>(null);
    const [prostayEnabled, setProstayEnabled] = useState(false);
    const [serviceId, setServiceId] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<ReservationStatus | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [assigning, setAssigning] = useState<Reservation | null>(null);

    // Field definitions + settings are property-level config: load once.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [f, s] = await Promise.all([
                ReservationService.getFieldDefinitions(),
                ReservationService.getSettings(),
            ]);
            if (cancelled) return;
            setFields((f || []).filter(x => x.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
            setSettings(s);
        })();
        return () => { cancelled = true; };
    }, []);

    // The PMS link lives on the company record, same as everywhere else that
    // needs to know whether hotel-guest lookup is available.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Company`, { credentials: 'include' });
                if (!res.ok) return;
                const company = await res.json();
                if (!cancelled) setProstayEnabled(!!company?.prostayApiKey);
            } catch { /* no PMS link assumed */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await ReservationService.filter({ date: dateKey });
            setReservations(data || []);
        } finally {
            setLoading(false);
        }
    }, [dateKey]);

    useEffect(() => { load(); }, [load]);

    const onDate = useMemo(
        () => reservations
            .filter(r => r.date === dateKey)
            .sort((a, b) => minutesOfTime(a.time) - minutesOfTime(b.time)),
        [reservations, dateKey],
    );

    const serviceWindowsEnabled = serviceWindowsOn(settings);

    // Ignore the selection outright when windows are off, so a filter left over
    // from before an admin disabled them can't hide the day's bookings.
    const activeService = useMemo(
        () => (serviceWindowsEnabled ? settings?.services.find(s => s.id === serviceId) || null : null),
        [serviceWindowsEnabled, settings, serviceId],
    );

    const inService = useCallback((r: Reservation): boolean => {
        if (!activeService) return true;
        const t = minutesOfTime(r.time);
        return t >= minutesOfTime(activeService.startTime) && t < minutesOfTime(activeService.endTime);
    }, [activeService]);

    const visible = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return onDate.filter(r => {
            if (r.status === 'waitlist') return false; // shown in the queue, not the day's list
            if (!inService(r)) return false;
            if (statusFilter !== 'all' && r.status !== statusFilter) return false;
            if (term) {
                const hay = [
                    r.code, r.guest?.firstName, r.guest?.lastName, r.guest?.phone,
                    r.guest?.email, r.prostay?.roomNumber,
                ].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [onDate, inService, statusFilter, searchTerm]);

    const counts = useMemo(() => {
        const base = Object.fromEntries(
            RESERVATION_STATUS_ORDER.map(s => [s, 0]),
        ) as Record<ReservationStatus | 'all', number>;
        base.waitlist = 0;
        const scoped = onDate.filter(r => r.status !== 'waitlist' && inService(r));
        scoped.forEach(r => { base[r.status] += 1; });
        base.all = scoped.length;
        base.waitlist = onDate.filter(r => r.status === 'waitlist').length;
        return base;
    }, [onDate, inService]);

    /**
     * The walk-in queue, front of the line first. Ordered by arrival, which is the
     * only order a room full of waiting guests will accept — seating out of turn is
     * a decision the host makes, not something the list decides for them.
     */
    const waitlist = useMemo(
        () => onDate
            .filter(r => r.status === 'waitlist')
            .sort((a, b) => (
                new Date(a.waitlist?.addedAt || 0).getTime() - new Date(b.waitlist?.addedAt || 0).getTime()
            )),
        [onDate],
    );

    const kpis = useMemo(() => {
        const live = onDate.filter(
            r => r.status !== 'waitlist' && inService(r) && !CLOSED_RESERVATION_STATUSES.includes(r.status),
        );
        const waiting = onDate.filter(r => r.status === 'waitlist');
        return {
            covers: live.reduce((s, r) => s + r.partySize, 0),
            bookings: live.length,
            unassigned: live.filter(r => r.tableIds.length === 0).length,
            seated: onDate.filter(r => r.status === 'seated').length,
            waiting: waiting.length,
            waitingCovers: waiting.reduce((s, r) => s + r.partySize, 0),
        };
    }, [onDate, inService]);

    // One badge per table: the soonest booking that still needs that table.
    const byTableId = useMemo(() => {
        const out: Record<string, Reservation> = {};
        onDate
            .filter(r => UPCOMING_STATUSES.includes(r.status))
            .forEach(r => {
                r.tableIds.forEach(id => {
                    const existing = out[id];
                    if (!existing || minutesOfTime(r.time) < minutesOfTime(existing.time)) out[id] = r;
                });
            });
        return out;
    }, [onDate]);

    // The sitting at each table, so the board measures it against the turn time
    // the booking was actually planned with rather than today's rules.
    const seatedByTableId = useMemo(() => {
        const out: Record<string, Reservation> = {};
        onDate
            .filter(r => r.status === 'seated')
            .forEach(r => { r.tableIds.forEach(id => { out[id] = r; }); });
        return out;
    }, [onDate]);

    const updateLocal = useCallback((r: Reservation | null) => {
        if (!r) return;
        setReservations(prev => {
            const i = prev.findIndex(x => x.id === r.id);
            if (i < 0) return [...prev, r];
            const next = [...prev];
            next[i] = r;
            return next;
        });
    }, []);

    const stepDate = useCallback((days: number) => {
        setDateKey(prev => {
            const d = new Date(`${prev}T12:00:00`);
            d.setDate(d.getDate() + days);
            return toDateKey(d);
        });
    }, []);

    const goToday = useCallback(() => setDateKey(toDateKey(new Date())), []);

    const startAssign = useCallback((r: Reservation) => setAssigning(r), []);
    const cancelAssign = useCallback(() => setAssigning(null), []);

    /**
     * Stays in assign mode when the server refuses the overlap, so the tables are
     * still staged and the warning has something to point at.
     */
    const assignTables = useCallback(async (
        tableIds: string[],
        targetFloorId?: string,
        allowOverlap = false,
    ): Promise<ReservationSaveResult> => {
        if (!assigning) return { ok: false, clashes: [] };
        const result = await ReservationService.assignTables(
            assigning.id, tableIds, targetFloorId || floorId || undefined, allowOverlap,
        );
        if (result.ok) {
            updateLocal(result.reservation);
            setAssigning(null);
        }
        return result;
    }, [assigning, floorId, updateLocal]);

    const joinWaitlist = useCallback(async (entry: {
        partySize: number;
        quotedMinutes: number;
        guest: Reservation['guest'];
        guestNotes?: string;
        customFields?: Record<string, unknown>;
    }) => {
        const saved = await ReservationService.joinWaitlist({
            ...entry,
            floorId: floorId || undefined,
        });
        updateLocal(saved);
        return saved;
    }, [floorId, updateLocal]);

    const updateWaiting = useCallback(async (r: Reservation, changes: {
        partySize: number;
        quotedMinutes: number;
        guest: Reservation['guest'];
        guestNotes?: string;
    }) => {
        const saved = await ReservationService.update({
            id: r.id,
            partySize: changes.partySize,
            // A bigger party needs the table for longer, and the quote the floor
            // suggests to everyone behind them is built on that number.
            durationMinutes: plannedTurnMinutes(settings, changes.partySize),
            guest: changes.guest,
            // Empty rather than absent, so deleting a note actually deletes it:
            // an undefined field is dropped on the way out and changes nothing.
            guestNotes: changes.guestNotes ?? '',
            waitlist: r.waitlist ? { ...r.waitlist, phone: changes.guest?.phone } : undefined,
        });
        // A changed quote goes through the re-quote path so the countdown restarts
        // from now, exactly as it would from the card.
        if (saved && changes.quotedMinutes !== r.waitlist?.quotedMinutes) {
            const requoted = await ReservationService.updateWaitlist(r.id, {
                quotedMinutes: changes.quotedMinutes,
            });
            updateLocal(requoted || saved);
            return requoted || saved;
        }
        updateLocal(saved);
        return saved;
    }, [settings, updateLocal]);

    const requoteWait = useCallback(async (r: Reservation, quotedMinutes: number) => {
        updateLocal(await ReservationService.updateWaitlist(r.id, { quotedMinutes }));
    }, [updateLocal]);

    const callWaiting = useCallback(async (r: Reservation, called = true) => {
        updateLocal(await ReservationService.updateWaitlist(r.id, { notified: called }));
    }, [updateLocal]);

    /** They gave up, or went elsewhere. Kept on the day with a reason, not deleted. */
    const leaveWaitlist = useCallback(async (r: Reservation) => {
        updateLocal(await ReservationService.cancel(r.id, 'Left before being seated'));
    }, [updateLocal]);

    /**
     * Extends the hold from now rather than from the booking's time, so a party
     * who rings at 19:40 to say they are ten minutes away is held from 19:40.
     */
    const holdLate = useCallback(async (r: Reservation) => {
        const minutes = lateGraceMinutes(settings);
        updateLocal(await ReservationService.update({
            id: r.id,
            heldUntil: new Date(Date.now() + minutes * 60000).toISOString(),
        }));
        return minutes;
    }, [settings, updateLocal]);

    const move = useCallback(async (
        r: Reservation,
        changes: { time?: string; tableIds?: string[] },
        allowOverlap = false,
    ): Promise<ReservationSaveResult> => {
        const result = await ReservationService.save({
            id: r.id,
            ...(changes.time !== undefined ? { time: changes.time } : {}),
            ...(changes.tableIds !== undefined ? { tableIds: changes.tableIds } : {}),
        }, allowOverlap);
        if (result.ok) updateLocal(result.reservation);
        return result;
    }, [updateLocal]);

    const setStatus = useCallback(async (r: Reservation, status: ReservationStatus) => {
        let saved: Reservation | null = null;
        if (status === 'arrived') saved = await ReservationService.markArrived(r.id);
        else if (status === 'confirmed') saved = await ReservationService.confirm(r.id);
        else if (status === 'noShow') saved = await ReservationService.markNoShow(r.id);
        else if (status === 'completed') saved = await ReservationService.complete(r.id);
        else if (status === 'cancelled') saved = await ReservationService.cancel(r.id);
        else saved = await ReservationService.update({ id: r.id, status });
        updateLocal(saved);
    }, [updateLocal]);

    return {
        dateKey,
        setDateKey,
        stepDate,
        goToday,
        isToday: dateKey === toDateKey(new Date()),
        reservations,
        onDate,
        loading,
        fields,
        settings,
        prostayEnabled,
        serviceWindowsEnabled,
        serviceId: serviceWindowsEnabled ? serviceId : 'all',
        setServiceId,
        statusFilter,
        setStatusFilter,
        searchTerm,
        setSearchTerm,
        visible,
        byTableId,
        seatedByTableId,
        counts,
        kpis,
        waitlist,
        joinWaitlist,
        updateWaiting,
        requoteWait,
        callWaiting,
        leaveWaitlist,
        holdLate,
        reload: load,
        assigning,
        startAssign,
        cancelAssign,
        assignTables,
        updateLocal,
        setStatus,
        move,
    };
}

export default useReservations;
